import React, { useCallback, useMemo, useRef, useState } from "react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import {
  CaseloadEngagementPeriod,
  CaseloadHomeworkClient,
  CaseloadMessageClient,
  CaseloadNoActivityClient,
} from "../api/contracts";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import { PortalTableColumn, StatusPill } from "../components/DataDisplay";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  CaseloadMetricCard,
  ReportHelpTooltip,
  ReportTableSection,
} from "../components/ReportVisuals";
import { clientProfilePath } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";
import { formatPortalDate } from "../utils/dateTime";

const periodCopy: Record<
  CaseloadEngagementPeriod,
  {
    label: string;
    engagementDescription: string;
    noActivityDescription: string;
    noActivityKpiLabel: string;
  }
> = {
  "this-week": {
    label: "This Week",
    engagementDescription:
      "Shows the most active clients this week based on user messages and completed homework.",
    noActivityDescription:
      "Shows activated clients with no tracked activity this week.",
    noActivityKpiLabel: "Clients With No Activity This Week",
  },
  "last-30-days": {
    label: "Last 30 Days",
    engagementDescription:
      "Shows the most active clients over the last 30 days based on user messages and completed homework.",
    noActivityDescription:
      "Shows activated clients with no tracked activity in the last 30 days.",
    noActivityKpiLabel: "Clients With No Activity Last 30 Days",
  },
};

export function formatEngagementDuration(seconds: number | null): string {
  if (seconds === null) return "—";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (!minutes) return `${remainingSeconds}s`;
  return remainingSeconds ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
}

export function CaseloadEngagementPage({
  clinicianCode,
}: {
  clinicianCode: string;
}) {
  const [period, setPeriod] = useState<CaseloadEngagementPeriod>("this-week");
  const tabRefs = useRef<
    Record<CaseloadEngagementPeriod, HTMLButtonElement | null>
  >({
    "this-week": null,
    "last-30-days": null,
  });
  const request = useCallback(
    (signal: AbortSignal) =>
      clinicianPortalApi.getCaseloadEngagement(clinicianCode, period, signal),
    [clinicianCode, period],
  );
  const state = usePortalData(request);
  const responseData =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const data = responseData?.period === period ? responseData : undefined;
  const report = data?.caseloadEngagement;
  const available = Boolean(data?.found && data.authorized && data.therapist);
  const copy = periodCopy[period];

  const messageColumns = useMemo<
    readonly PortalTableColumn<CaseloadMessageClient>[]
  >(
    () => [
      {
        key: "access-code",
        header: "Access Code",
        initialWidth: 150,
        render: (client) => <strong>{client.accessCode}</strong>,
      },
      {
        key: "nickname",
        header: "Nickname",
        initialWidth: 150,
        render: (client) => client.nickname,
      },
      {
        key: "messages",
        header: (
          <span className="cp-report-label-with-tooltip">
            Client Interactions
            <ReportHelpTooltip
              label="Client Interactions"
              definition="Number of messages the client typed or spoke to the OCD Homework Assistant during this reporting period."
              placement="bottom"
            />
          </span>
        ),
        headerLabel: "Client Interactions",
        initialWidth: 180,
        render: (client) => client.userMessageCount,
      },
    ],
    [],
  );

  const homeworkColumns = useMemo<
    readonly PortalTableColumn<CaseloadHomeworkClient>[]
  >(
    () => [
      {
        key: "access-code",
        header: "Access Code",
        initialWidth: 150,
        render: (client) => <strong>{client.accessCode}</strong>,
      },
      {
        key: "nickname",
        header: "Nickname",
        initialWidth: 250,
        render: (client) => client.nickname,
      },
      {
        key: "completed",
        header: "Completed",
        initialWidth: 135,
        render: (client) => client.completedHomeworkCount,
      },
      {
        key: "total-time",
        header: "Total Time",
        initialWidth: 150,
        render: (client) =>
          formatEngagementDuration(client.completedHomeworkSeconds),
      },
      {
        key: "average-time",
        header: "Average Time",
        initialWidth: 150,
        render: (client) =>
          formatEngagementDuration(client.averageHomeworkSeconds),
      },
    ],
    [],
  );

  const noActivityColumns = useMemo<
    readonly PortalTableColumn<CaseloadNoActivityClient>[]
  >(
    () => [
      {
        key: "access-code",
        header: "Access Code",
        initialWidth: 150,
        render: (client) => <strong>{client.accessCode}</strong>,
      },
      {
        key: "nickname",
        header: "Nickname",
        initialWidth: 240,
        render: (client) => client.nickname,
      },
      {
        key: "status",
        header: "Status",
        initialWidth: 145,
        render: (client) => <StatusPill status={client.status} />,
      },
      {
        key: "last-active",
        header: "Last Active Date",
        initialWidth: 180,
        render: (client) => formatPortalDate(client.lastActiveDate),
      },
      {
        key: "days-since-active",
        header: "Days Since Active",
        initialWidth: 175,
        render: (client) => client.daysSinceActive ?? "—",
      },
    ],
    [],
  );

  const selectPeriod = (nextPeriod: CaseloadEngagementPeriod) => {
    if (nextPeriod !== period) setPeriod(nextPeriod);
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="caseload-engagement"
      clinicianCode={clinicianCode}
      therapistRole={responseData?.therapist?.role}
    >
      <PortalHeader
        title="Caseload Insights"
        clinicianName={responseData?.therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[{ label: "Reports" }, { label: "Caseload Insights" }]}
          />
        }
      />

      <div className="cp-page-content cp-page-content--wide cp-page-content--profile">
        <div
          className="cp-caseload-period-tabs"
          role="tablist"
          aria-label="Caseload engagement period"
        >
          {(["this-week", "last-30-days"] as CaseloadEngagementPeriod[]).map(
            (option) => (
              <button
                key={option}
                id={`caseload-period-${option}`}
                type="button"
                role="tab"
                aria-selected={period === option}
                aria-controls="caseload-engagement-panel"
                tabIndex={period === option ? 0 : -1}
                ref={(element) => {
                  tabRefs.current[option] = element;
                }}
                onClick={() => selectPeriod(option)}
                onKeyDown={(event) => {
                  if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
                    return;
                  }
                  event.preventDefault();
                  const next =
                    period === "this-week" ? "last-30-days" : "this-week";
                  selectPeriod(next);
                  tabRefs.current[next]?.focus();
                }}
              >
                {periodCopy[option].label}
              </button>
            ),
          )}
        </div>

        <div
          id="caseload-engagement-panel"
          role="tabpanel"
          aria-labelledby={`caseload-period-${period}`}
        >
          {state.status === "loading" ||
          (state.status === "refreshing" && !data) ? (
            <LoadingState
              label={`Loading ${copy.label.toLowerCase()} caseload engagement`}
            />
          ) : null}
          {state.status === "error" ? (
            <ErrorState error={state.error} onRetry={state.refresh} />
          ) : null}
          {data && !available ? (
            <ErrorState
              error={new Error("Caseload engagement is not available")}
              onRetry={state.refresh}
            />
          ) : null}

          {available && report ? (
            <div className="cp-caseload-report">
              <section aria-labelledby="caseload-engagement-heading">
                <div className="cp-content-header">
                  <div>
                    <h2 id="caseload-engagement-heading">
                      Client Engagement Across My Clients — {copy.label}
                    </h2>
                    <p>{copy.engagementDescription}</p>
                    <p className="cp-caseload-report__window">
                      Reporting window:{" "}
                      {formatPortalDate(report.windowStartDate)}
                      {" – "}
                      {formatPortalDate(report.windowEndDate)} · As of{" "}
                      {formatPortalDate(report.asOfDate)} · {report.timezone}
                    </p>
                  </div>
                </div>

                <div className="cp-caseload-activity-grid">
                  <CaseloadMetricCard
                    label="Clients Who Interacted with the OCD Homework Assistant"
                    value={report.summary.clientsWithMessageActivity}
                    tone="messages"
                  />
                  <CaseloadMetricCard
                    label="Clients Who Completed Homework"
                    value={report.summary.clientsWithHomeworkActivity}
                    tone="homework"
                  />
                  <ReportTableSection
                    className="cp-caseload-report-table"
                    title="Most Active Clients in the OCD Homework Assistant"
                    emptyMessage="No message activity during this period."
                    columns={messageColumns}
                    records={report.topClientsByMessages}
                    getRecordKey={(client) => client.clientId}
                    onRecordOpen={(client) =>
                      navigateWithinPortal(
                        withClinicianCode(
                          clientProfilePath(client.clientId),
                          clinicianCode,
                        ),
                      )
                    }
                    getRecordOpenLabel={(client) =>
                      `Open client record for ${client.nickname}`
                    }
                  />
                  <ReportTableSection
                    className="cp-caseload-report-table"
                    title="Top Clients by Completed Homework"
                    emptyMessage="No completed homework during this period."
                    columns={homeworkColumns}
                    records={report.topClientsByCompletedHomework}
                    getRecordKey={(client) => client.clientId}
                    onRecordOpen={(client) =>
                      navigateWithinPortal(
                        withClinicianCode(
                          clientProfilePath(client.clientId),
                          clinicianCode,
                        ),
                      )
                    }
                    getRecordOpenLabel={(client) =>
                      `Open client record for ${client.nickname}`
                    }
                  />
                </div>
              </section>

              <section aria-labelledby="caseload-no-activity-heading">
                <div className="cp-content-header">
                  <div>
                    <h2 id="caseload-no-activity-heading">
                      Clients With No Activity — {copy.label}
                    </h2>
                    <p>{copy.noActivityDescription}</p>
                  </div>
                </div>

                <div className="cp-caseload-no-activity-column">
                  <div className="cp-caseload-kpi-grid cp-caseload-kpi-grid--single">
                    <CaseloadMetricCard
                      label={
                        report.summary.clientsWithNoActivity > 0
                          ? copy.noActivityKpiLabel
                          : "All eligible clients had activity."
                      }
                      value={report.summary.clientsWithNoActivity}
                      tone={
                        report.summary.clientsWithNoActivity > 0
                          ? "inactive"
                          : "inactive-clear"
                      }
                    />
                  </div>

                  {report.summary.clientsWithNoActivity > 0 ? (
                    <ReportTableSection
                      className="cp-caseload-report-table"
                      title="Clients With No Activity"
                      emptyMessage="No client details are available."
                      columns={noActivityColumns}
                      records={report.clientsWithNoActivity}
                      getRecordKey={(client) => client.clientId}
                      onRecordOpen={(client) =>
                        navigateWithinPortal(
                          withClinicianCode(
                            clientProfilePath(client.clientId),
                            clinicianCode,
                          ),
                        )
                      }
                      getRecordOpenLabel={(client) =>
                        `Open client record for ${client.nickname}`
                      }
                    />
                  ) : null}
                </div>
              </section>
            </div>
          ) : null}
        </div>
      </div>
    </ClinicianPortalLayout>
  );
}
